'use client';

import Link from 'next/link';
import { ScrollReveal } from './ScrollReveal';

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative">
        {/* Main footer content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
            {/* Brand column */}
            <div className="lg:col-span-5 space-y-6">
              <ScrollReveal>
                <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">
                  Andrew McCourt
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <p className="text-primary-foreground/70 text-lg leading-relaxed max-w-md">
                  Guiding individuals, churches and organizations toward meaningful growth through
                  strategic insight and faith-based leadership.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <div className="flex items-center gap-4 pt-2">
                  <span className="w-12 h-px bg-accent/50" />
                  <span className="text-sm text-primary-foreground/50 italic">
                    30+ years in ministry
                  </span>
                </div>
              </ScrollReveal>
            </div>

            {/* Navigation columns */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                {/* Services */}
                <ScrollReveal delay={150}>
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium tracking-wider uppercase text-accent">
                      Services
                    </h3>
                    <ul className="space-y-3">
                      <li>
                        <Link
                          href="/speaking"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200"
                        >
                          Speaking
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/consulting"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200"
                        >
                          Consulting
                        </Link>
                      </li>
                    </ul>
                  </div>
                </ScrollReveal>

                {/* Quick Links */}
                <ScrollReveal delay={200}>
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium tracking-wider uppercase text-accent">
                      Quick Links
                    </h3>
                    <ul className="space-y-3">
                      <li>
                        <Link
                          href="/#about"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200"
                        >
                          About
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/#sermons"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200"
                        >
                          Sermons
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/#contact"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200"
                        >
                          Contact
                        </Link>
                      </li>
                    </ul>
                  </div>
                </ScrollReveal>

                {/* Connect */}
                <ScrollReveal delay={250}>
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium tracking-wider uppercase text-accent">
                      Connect
                    </h3>
                    <ul className="space-y-3">
                      <li>
                        <a
                          href="https://www.instagram.com/andrewmccourt1/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200 inline-flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          Instagram
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.baysideonline.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-foreground/70 hover:text-accent transition-colors duration-200 inline-flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                          Bayside Church
                        </a>
                      </li>
                    </ul>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
            <p className="text-sm text-primary-foreground/50 text-center">
              &copy; {currentYear} Andrew McCourt. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
